import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";

const Stars = ({ value, size = "h-4 w-4" }) => (
  <span className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} ${i <= value ? "fill-amber-400 text-amber-400" : "fill-slate-100 text-slate-200"}`} />
    ))}
  </span>
);

export const ReviewsSection = ({ itemType, itemSlug }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, lang, fmtDate } = useLanguage();
  const [data, setData] = useState({ reviews: [], average: null, count: 0 });
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/reviews", { params: { item_type: itemType, item_slug: itemSlug } })
      .then((r) => setData(r.data))
      .catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info(t("Connecte-toi pour laisser un avis"));
      navigate("/connexion");
      return;
    }
    setBusy(true);
    try {
      await api.post("/reviews", { item_type: itemType, item_slug: itemSlug, rating, comment });
      toast.success(t("Merci pour ton avis !"));
      setComment("");
      setRating(5);
      load();
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reviews-section">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-bold text-slate-900">{t("Avis")}</h2>
        {data.count > 0 && (
          <span className="flex items-center gap-1.5 text-sm text-slate-500" data-testid="reviews-summary">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <strong className="text-slate-900">{lang === "en" ? data.average : String(data.average).replace(".", ",")}</strong>· {data.count} {t("avis")}
          </span>
        )}
      </div>

      <form onSubmit={submit} className="mt-4 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <button
              key={i}
              type="button"
              data-testid={`review-star-${i}`}
              onClick={() => setRating(i)}
              aria-label={lang === "en" ? `${i} star${i > 1 ? "s" : ""}` : `${i} étoile${i > 1 ? "s" : ""}`}
              className="p-1 transition hover:scale-110"
            >
              <Star className={`h-6 w-6 ${i <= rating ? "fill-amber-400 text-amber-400" : "fill-slate-100 text-slate-200"}`} />
            </button>
          ))}
        </div>
        <textarea
          data-testid="review-comment"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={user ? t("Raconte ton expérience…") : t("Connecte-toi pour laisser un avis")}
          className="mt-3 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100"
        />
        <button
          type="submit"
          data-testid="review-submit"
          disabled={busy}
          className="mt-3 rounded-full bg-[#2563EB] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] active:scale-95 disabled:opacity-60"
        >
          {busy ? t("Publication…") : t("Publier mon avis")}
        </button>
      </form>

      <div className="mt-5 space-y-3">
        {data.reviews.length === 0 ? (
          <p data-testid="reviews-empty" className="rounded-2xl bg-white p-6 text-sm text-slate-500">
            {t("Sois le premier à laisser un avis !")}
          </p>
        ) : (
          data.reviews.map((r, i) => (
            <div key={i} data-testid={`review-item-${i}`} className="rounded-2xl border border-slate-100 bg-white p-4">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EFF6FF] text-sm font-bold text-[#2563EB]">
                  {r.user_name?.[0]?.toUpperCase() || "A"}
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">{r.user_name}</p>
                  <p className="text-xs text-slate-400">{fmtDate(r.created_at?.slice(0, 10), { day: "numeric", month: "long", year: "numeric" })}</p>
                </div>
                <Stars value={r.rating} />
              </div>
              {r.comment && <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{r.comment}</p>}
              {r.reply && (
                <div className="mt-3 rounded-xl bg-[#EFF6FF] p-3" data-testid={`review-reply-${i}`}>
                  <p className="text-[10px] font-bold text-[#2563EB]">{t("Réponse du propriétaire")}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{r.reply.text}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
